/**
 * Template Library Page
 *
 * Browse, create, and manage specification templates.
 * System templates are read-only; user-created templates can be edited.
 *
 * Part of PowerSpec V3 - Custom Template Builder
 *
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Modal,
  Badge,
  Spinner,
  Alert,
  Breadcrumb,
  TextInput
} from 'flowbite-react';
import {
  HiPlus,
  HiHome,
  HiTemplate,
  HiLockClosed,
  HiSearch,
  HiStar,
  HiChartBar
} from 'react-icons/hi';
import TemplateBuilder from '../components/TemplateBuilder';
import {
  getSpecificationTemplates,
  createSpecificationTemplate,
  updateSpecificationTemplate,
  type SpecificationTemplate,
  type CreateTemplateRequest
} from '../api/specifications';
import { useAuth } from '../contexts/AuthContext';

// ---------------------------------------------------------------------------
// Category display helpers
// ---------------------------------------------------------------------------

const CATEGORY_COLORS: Record<string, string> = {
  root_cause: 'failure',
  failure_mode: 'warning',
  agile: 'info',
  process_improvement: 'success',
  technical: 'purple',
  product: 'indigo',
  compliance: 'pink',
  custom: 'gray',
};

const CATEGORY_LABELS: Record<string, string> = {
  root_cause: 'Root Cause',
  failure_mode: 'Failure Mode',
  agile: 'Agile',
  process_improvement: 'Process Improvement',
  technical: 'Technical',
  product: 'Product',
  compliance: 'Compliance',
  custom: 'Custom',
};

function getCategoryColor(category: string): string {
  return CATEGORY_COLORS[category] || 'gray';
}

function getCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category] || category;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TemplateLibraryPage: React.FC = () => {
  const { selectedClientId } = useAuth();

  // Data state
  const [templates, setTemplates] = useState<SpecificationTemplate[]>([]);
  const [templatesByCategory, setTemplatesByCategory] = useState<Record<string, SpecificationTemplate[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [showBuilderModal, setShowBuilderModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<SpecificationTemplate | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getSpecificationTemplates({ include_structure: true });

      if (response.success && response.data?.Records?.[0]) {
        const dataWrapper = response.data.Records[0] as unknown as {
          Records: SpecificationTemplate[];
          by_category: Record<string, SpecificationTemplate[]>;
        };
        setTemplates(dataWrapper.Records || []);
        setTemplatesByCategory(dataWrapper.by_category || {});
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load templates';
      setError(msg);
      console.error('[TemplateLibraryPage] Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // ---------------------------------------------------------------------------
  // Filtered view
  // ---------------------------------------------------------------------------

  const filteredByCategory = (() => {
    if (!searchQuery.trim()) return templatesByCategory;

    const q = searchQuery.toLowerCase();
    const filtered: Record<string, SpecificationTemplate[]> = {};

    for (const [cat, list] of Object.entries(templatesByCategory)) {
      const matches = list.filter(
        t =>
          t.name.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q)
      );
      if (matches.length > 0) {
        filtered[cat] = matches;
      }
    }
    return filtered;
  })();

  // ---------------------------------------------------------------------------
  // Template create / update
  // ---------------------------------------------------------------------------

  const handleCreateClick = () => {
    setEditingTemplate(undefined);
    setShowBuilderModal(true);
  };

  const handleEditClick = (template: SpecificationTemplate) => {
    if (template.is_system_template) return;
    setEditingTemplate(template);
    setShowBuilderModal(true);
  };

  const handleBuilderSave = async (data: {
    name: string;
    description: string;
    category: string;
    is_public: boolean;
    structure: Record<string, unknown>;
  }) => {
    if (!selectedClientId) {
      setError('No client selected');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (editingTemplate) {
        // Update
        await updateSpecificationTemplate(editingTemplate.id, {
          Client_ID: selectedClientId,
          name: data.name,
          description: data.description,
          category: data.category,
          is_public: data.is_public,
          structure: data.structure,
        });
      } else {
        // Create
        const createData: CreateTemplateRequest = {
          Client_ID: selectedClientId,
          name: data.name,
          description: data.description,
          category: data.category,
          is_public: data.is_public,
          structure: data.structure,
        };
        await createSpecificationTemplate(createData);
      }

      setShowBuilderModal(false);
      setEditingTemplate(undefined);
      await fetchTemplates();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save template';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleBuilderCancel = () => {
    setShowBuilderModal(false);
    setEditingTemplate(undefined);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <Breadcrumb className="mb-4">
        <Breadcrumb.Item href="/dashboard" icon={HiHome}>
          Dashboard
        </Breadcrumb.Item>
        <Breadcrumb.Item icon={HiTemplate}>
          Specification Templates
        </Breadcrumb.Item>
      </Breadcrumb>

      {/* Error */}
      {error && (
        <Alert color="failure" className="mb-4" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Specification Templates</h1>
          <p className="text-gray-600">
            Browse system templates or create your own custom templates
          </p>
        </div>
        <Button color="blue" onClick={handleCreateClick}>
          <HiPlus className="w-5 h-5 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <TextInput
          icon={HiSearch}
          placeholder="Search templates by name or description..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Template grid grouped by category */}
      {Object.keys(filteredByCategory).length === 0 ? (
        <Card>
          <p className="text-gray-500 text-center py-8">
            {searchQuery
              ? 'No templates match your search.'
              : 'No templates available yet. Create your first custom template!'}
          </p>
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(filteredByCategory).map(([category, categoryTemplates]) => (
            <div key={category}>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-lg font-semibold text-gray-900">
                  {getCategoryLabel(category)}
                </h2>
                <Badge color={getCategoryColor(category) as 'gray'} size="sm">
                  {categoryTemplates.length}
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryTemplates.map(tmpl => (
                  <Card
                    key={tmpl.id}
                    className={`cursor-pointer hover:shadow-lg transition-shadow ${
                      tmpl.is_system_template ? '' : 'border-l-4 border-l-blue-500'
                    }`}
                    onClick={() => handleEditClick(tmpl)}
                  >
                    <div className="space-y-3">
                      {/* Title row */}
                      <div className="flex items-start justify-between">
                        <h3 className="text-base font-semibold text-gray-900 line-clamp-1">
                          {tmpl.name}
                        </h3>
                        {tmpl.is_system_template && (
                          <HiLockClosed
                            className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2"
                            title="System template (read-only)"
                          />
                        )}
                      </div>

                      {/* Description */}
                      <p className="text-sm text-gray-500 line-clamp-2">
                        {tmpl.description || 'No description'}
                      </p>

                      {/* Footer stats */}
                      <div className="flex items-center gap-4 text-xs text-gray-400 pt-2 border-t">
                        <span className="flex items-center gap-1">
                          <HiChartBar className="w-3 h-3" />
                          {tmpl.usage_count} uses
                        </span>
                        {tmpl.rating_average !== null && tmpl.rating_average > 0 && (
                          <span className="flex items-center gap-1">
                            <HiStar className="w-3 h-3" />
                            {Number(tmpl.rating_average).toFixed(1)}
                          </span>
                        )}
                        <Badge
                          color={getCategoryColor(tmpl.category) as 'gray'}
                          size="xs"
                        >
                          {getCategoryLabel(tmpl.category)}
                        </Badge>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Builder Modal */}
      <Modal
        show={showBuilderModal}
        onClose={handleBuilderCancel}
        size="7xl"
        dismissible
      >
        <Modal.Header>
          {editingTemplate ? 'Edit Template' : 'Create Custom Template'}
        </Modal.Header>
        <Modal.Body>
          <TemplateBuilder
            template={editingTemplate}
            onSave={handleBuilderSave}
            onCancel={handleBuilderCancel}
          />
          {saving && (
            <div className="flex items-center justify-center py-4">
              <Spinner size="sm" className="mr-2" />
              <span className="text-gray-600">Saving...</span>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default TemplateLibraryPage;
